// Legals / audit-readiness rules: which patient forms and staff documents are
// overdue, coming due, or missing.
//
// Mirrors api/_lib/compliance.js, which the daily digest uses. Keep the two in
// step; compliance.parity.test.ts fails if they drift.

export type ComplianceStatus = 'overdue' | 'due_soon' | 'incomplete';

export const DUE_SOON_DAYS = 30;

// Months a completed patient form stays valid before it must be re-signed.
// Anything not listed falls back to DEFAULT_RENEWAL_BY_CATEGORY. null = never renews.
export const RENEWAL_MONTHS: Record<string, number | null> = {
  'standard-intake': 12,
  'financial-responsibility': 12,
  'release-information': 12,
  'consent-telehealth': 12,
  'consent-ai': 12,
  'consent-audio': 12,
  'consent-cc': 12,
  'consent-informed': 12,
  'consent-privacy': 12,
  'consent-minor-software': 12,
  'covid-screening': null,
  gad7: null,
  phq9: null,
  'behavioral-treatment-plan': null,
  'couples-treatment-plan': null,
};

export const DEFAULT_RENEWAL_BY_CATEGORY: Record<string, number | null> = {
  intake: 12,
  consent: 12,
};

export interface FormInput {
  formTemplateId: string;
  category?: string;
  required?: boolean;
  completed: boolean;
  completedDate?: string | null;
  dueDate?: string | null;
}

export interface HrInput {
  status: string;
  dueDate?: string | null;
  expirationDate?: string | null;
}

export interface Evaluation {
  status: ComplianceStatus;
  /** The date the item is (or was) due, ISO string. */
  dueDate?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function parse(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

function classifyDate(due: Date, now: Date): ComplianceStatus | null {
  if (due.getTime() < now.getTime()) return 'overdue';
  if (due.getTime() - now.getTime() <= DUE_SOON_DAYS * DAY_MS) return 'due_soon';
  return null;
}

export function renewalMonthsFor(formTemplateId: string, category?: string): number | null {
  if (formTemplateId in RENEWAL_MONTHS) return RENEWAL_MONTHS[formTemplateId];
  if (category && category in DEFAULT_RENEWAL_BY_CATEGORY) return DEFAULT_RENEWAL_BY_CATEGORY[category];
  return null;
}

export function evaluateForm(form: FormInput, now: Date = new Date()): Evaluation | null {
  if (!form.completed) {
    const due = parse(form.dueDate);
    if (due && due.getTime() < now.getTime()) {
      return { status: 'overdue', dueDate: due.toISOString() };
    }
    if (form.required) {
      return { status: 'incomplete', ...(due ? { dueDate: due.toISOString() } : {}) };
    }
    return null;
  }

  const months = renewalMonthsFor(form.formTemplateId, form.category);
  const completedOn = parse(form.completedDate);
  if (months === null || !completedOn) return null;

  const renewOn = addMonths(completedOn, months);
  const status = classifyDate(renewOn, now);
  return status ? { status, dueDate: renewOn.toISOString() } : null;
}

export function evaluateHrDocument(doc: HrInput, now: Date = new Date()): Evaluation | null {
  const expires = parse(doc.expirationDate);
  if (doc.status === 'expired') {
    return { status: 'overdue', ...(expires ? { dueDate: expires.toISOString() } : {}) };
  }
  if (doc.status === 'pending' || doc.status === 'rejected') {
    const due = parse(doc.dueDate);
    if (due && due.getTime() < now.getTime()) {
      return { status: 'overdue', dueDate: due.toISOString() };
    }
    return { status: 'incomplete', ...(due ? { dueDate: due.toISOString() } : {}) };
  }
  if (expires) {
    const status = classifyDate(expires, now);
    if (status) return { status, dueDate: expires.toISOString() };
  }
  return null;
}

export interface ComplianceCounts {
  overdue: number;
  due_soon: number;
  incomplete: number;
  total: number;
}

export function summarize(evaluations: Array<Evaluation | null>): ComplianceCounts {
  const counts: ComplianceCounts = { overdue: 0, due_soon: 0, incomplete: 0, total: 0 };
  for (const e of evaluations) {
    if (!e) continue;
    counts[e.status] += 1;
    counts.total += 1;
  }
  return counts;
}
